import { c as _c } from "react/compiler-runtime";
import { useValueRef } from 'design-system/client';
import analytics, { getDonationFormType } from 'product-analytics';
import type { CodonationChangeData } from 'product-analytics';
import { useCallback, useEffect, useReducer } from 'react';
import { useDonationFormConfig } from '../../DonationFormConfigContext';
import useDonationFormValues from '../useDonationFormValues';
import CodonationCalculator from '@/donationForm/_dependencies/CodonationCalculator';
export const ActionTypes = {
  Change: 'change',
  Reset: 'reset',
  AmountChanged: 'amount_changed'
} as const;
export function ResetAction() {
  return {
    type: ActionTypes.Reset
  };
}
export function AmountChangedAction(value: number) {
  return {
    type: ActionTypes.AmountChanged,
    value
  };
}
export function ChangeAction(value: number) {
  return {
    type: ActionTypes.Change,
    value
  };
}
export type Action = ReturnType<typeof ResetAction> | ReturnType<typeof AmountChangedAction> | ReturnType<typeof ChangeAction>;
export type CodonationChangeOwnData = Pick<CodonationChangeData, 'donation_amount' | 'codonation_amount' | 'bucket_index' | 'no_of_increases' | 'no_of_decreases' | 'percent_of_bucket_bracket' | 'percent_of_max' | 'max' | 'step'>;
export function getPercentageFromIndex(values: readonly number[], index: number | null, value: number, max: number) {
  let i = index ?? -1;
  const start = index !== null ? values[index] : 0;
  let nextValue: number | undefined = value;
  if (start === undefined) return null;
  do {
    i++;
    nextValue = values[i];
    if (nextValue === undefined) {
      nextValue = max;
      break;
    }
  } while (nextValue <= start);
  if (!nextValue) return null;
  const percentage = Math.round((value - start) / (nextValue - start) * 100);
  return percentage;
}
export function calculateCodonationChangeData(state: CodonationChangeOwnData): CodonationChangeOwnData {
  const {
    findBucketIndexByValue
  } = CodonationCalculator.getCodonationHelpers(state.donation_amount);
  const bucketIndex = findBucketIndexByValue(state.codonation_amount);
  const config = CodonationCalculator.findConfig(state.donation_amount);
  const percentOfTheBucket = getPercentageFromIndex(config.minBucketValue, bucketIndex, state.codonation_amount, state.max);
  return {
    ...state,
    bucket_index: bucketIndex,
    percent_of_bucket_bracket: percentOfTheBucket,
    percent_of_max: Math.round(state.codonation_amount / state.max * 100)
  };
}
function getInitialState({
  coDonationAmount,
  amountInCents
}: {
  coDonationAmount: number;
  amountInCents: number;
}): CodonationChangeOwnData {
  const max = CodonationCalculator.max(amountInCents);
  const step = CodonationCalculator.step(amountInCents);
  return {
    donation_amount: amountInCents,
    codonation_amount: coDonationAmount,
    no_of_increases: 0,
    no_of_decreases: 0,
    bucket_index: null,
    percent_of_bucket_bracket: null,
    percent_of_max: Math.round(coDonationAmount / max * 100),
    max,
    step
  };
}
export function reducer(prev: CodonationChangeOwnData, action: Action): CodonationChangeOwnData {
  switch (action.type) {
    case ActionTypes.Change:
      {
        if (prev.codonation_amount === action.value) return prev;
        const no_of_increases = prev.codonation_amount < action.value ? prev.no_of_increases + 1 : prev.no_of_increases;
        const no_of_decreases = prev.codonation_amount > action.value ? prev.no_of_decreases + 1 : prev.no_of_decreases;
        return calculateCodonationChangeData({
          ...prev,
          codonation_amount: action.value,
          no_of_increases,
          no_of_decreases
        });
      }
    case ActionTypes.Reset:
      return getInitialState({
        coDonationAmount: prev.codonation_amount,
        amountInCents: prev.donation_amount
      });
    case ActionTypes.AmountChanged:
      if (isNaN(action.value)) return prev;
      return calculateCodonationChangeData({
        ...prev,
        max: CodonationCalculator.max(action.value),
        step: CodonationCalculator.step(action.value),
        no_of_increases: 0,
        no_of_decreases: 0,
        donation_amount: action.value
      });
    default:
      return prev;
  }
}
function reducerWithLogging(state: CodonationChangeOwnData, action: Action): CodonationChangeOwnData {
  const next = reducer(state, action);
  return next;
}
function useCodonationAnalytics(initialCodonationAmout) {
  const $ = _c(17);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["amount_cents"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [amountInCents] = useDonationFormValues(t0);
  let t1;
  if ($[1] !== amountInCents || $[2] !== initialCodonationAmout) {
    t1 = {
      coDonationAmount: initialCodonationAmout,
      amountInCents
    };
    $[1] = amountInCents;
    $[2] = initialCodonationAmout;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [data, dispatch] = useReducer(reducerWithLogging, t1, getInitialState);
  const latestData = useValueRef(data);
  let t2;
  let t3;
  if ($[4] !== amountInCents) {
    t2 = () => {
      dispatch(AmountChangedAction(amountInCents));
    };
    t3 = [amountInCents];
    $[4] = amountInCents;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const {
    receiverId,
    receiverType,
    channel,
    showCodonation
  } = useDonationFormConfig();
  let t4;
  if ($[7] !== channel || $[8] !== latestData || $[9] !== receiverId || $[10] !== receiverType || $[11] !== showCodonation) {
    t4 = () => {
      analytics.coDonationChange({
        ...latestData.current,
        id: receiverId,
        receiver_type: receiverType,
        donation_form_type: getDonationFormType({
          receiverId,
          receiverType,
          channel,
          showCodonation
        })
      });
      dispatch(ResetAction());
    };
    $[7] = channel;
    $[8] = latestData;
    $[9] = receiverId;
    $[10] = receiverType;
    $[11] = showCodonation;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const commitHandler = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      dispatch(ResetAction());
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const reset = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value => {
      dispatch(ChangeAction(value));
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const changeValue = t6;
  let t7;
  if ($[15] !== commitHandler) {
    t7 = {
      commitHandler,
      reset,
      changeValue
    };
    $[15] = commitHandler;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export default useCodonationAnalytics;